import router from '@/router';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5059/api';

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  success: boolean;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedResult<T> {
  data: T[];
  pagination: PaginationInfo;
}

export class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('accessToken');

      const headers: Record<string, string> = {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(options.headers as Record<string, string> | undefined),
      };
      if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
      const config: RequestInit = {
        headers,
        ...options,
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, config);

      if (response.status === 401) {
        router.push('/login');
      } else if (response.status === 404) {
        router.push('/dashboard');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      if (response.status === 204) {
        return {
          success: true,
          data: undefined,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  async requestAbsolute<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('accessToken');

      const headers: Record<string, string> = {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(options.headers as Record<string, string> | undefined),
      };
      if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
      const config: RequestInit = {
        headers,
        ...options,
      };

      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }
}

export const apiService = new ApiService();

export * from './types';

// Dynamically load API extension modules. This function should be
// invoked during application startup so that the prototype methods
// defined in each extension are registered before use.
export function loadApiExtensions() {
  return Promise.all([
    import('./authApi'),
    import('./tenantApi'),
    import('./serviceApi'),
    import('./dataApi'),
    import('./flowApi'),
    import('./domainVerificationApi'),
    import('./searchApi'),
    import('./auditApi'),
    import('./auditLogApi'),
  ]);
}

export default apiService;
